import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import {
  getFilteredByValue,
  getFilteredByValues,
  saveRecords,
} from "./utils/array.repository.utils";
import { InspectionRepository } from "./inspection.repository";
import { Inspection } from "../types/Inspection/Inspection";
import { Inspections } from "../types/Inspection/Inspections";

@Injectable({
  providedIn: "root",
})
export class TabDataRepository extends InspectionRepository {
  private childRecordsSubjectMap = new Map<string, BehaviorSubject<any[]>>();

  public getChildRecords(inspection: Inspection, tabName: string) {
    return inspection.tabs[tabName] || [];
  }

  public getChildRecordsObservable(
    inspection: Inspection,
    tabName: string,
  ): Observable<any[]> {
    const records = this.getChildRecords(inspection, tabName);
    if (!this.childRecordsSubjectMap.has(tabName)) {
      this.childRecordsSubjectMap.set(
        tabName,
        new BehaviorSubject<any[]>(records),
      );
    } else {
      this.childRecordsSubjectMap.get(tabName)?.next(records);
    }
    return (
      this.childRecordsSubjectMap.get(tabName)?.asObservable() ||
      new Observable()
    );
  }

  public getFilteredChildRecords(
    inspection: Inspection,
    tabName: string,
    key: string,
    value: string | number,
  ) {
    const data = this.getChildRecords(inspection, tabName);
    const filtered = getFilteredByValue(key, value, data);
    return filtered;
  }

  public getChildRecordsFilteredByValues(
    inspection: Inspection,
    tabName: string,
    key: string[],
    value: any[],
  ) {
    const data = this.getChildRecords(inspection, tabName);
    const filtered = getFilteredByValues(key, value, data);
    return filtered;
  }

  public async removeMultipleChildRecords(
    inspection: Inspection,
    tabName: string,
    key: string,
    value: any,
  ) {
    const data = this.getChildRecords(inspection, tabName);
    const filtered = data.filter((item: any) => !value.includes(item[key]));
    const updatedInspection = {
      ...inspection,
      tabs: { ...inspection.tabs, [tabName]: filtered },
    };
    const updatedRecords: Inspections = await super.saveInspection(
      updatedInspection,
    );
    this.childRecordsSubjectMap.get(tabName)?.next(filtered);
    return updatedRecords[inspection.inspectionID].tabs[tabName];
  }

  public async saveChildRecords(
    inspection: Inspection,
    tabName: string,
    primaryKey: string,
    records: any | any[],
  ): Promise<any[]> {
    const previous = this.getChildRecords(inspection, tabName);
    const setter = (values: any) => values;
    const updatedTabs = await saveRecords(
      primaryKey,
      Array.isArray(records) ? records : [records],
      setter,
      previous,
    );
    const updatedInspection = {
      ...inspection,
      tabs: { ...inspection.tabs, [tabName]: updatedTabs },
    };
    const updatedRecords: Inspections = await super.saveInspection(
      updatedInspection,
    );
    this.childRecordsSubjectMap.get(tabName)?.next(updatedTabs);
    return updatedRecords[inspection.inspectionID].tabs[tabName];
  }
}
